import { TextNode, ParagraphNode } from 'lexical';
import { $isHeadingNode, HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { $createDetailRequestButtonNode, $isDetailRequestButtonNode } from './nodes/DetailRequestButtonNode';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';

// Track which headings already have buttons to prevent duplicates
const headingsWithButtons = new WeakSet();

/**
 * Transform function that manages detail request buttons for headings
 * Following the official Lexical guide pattern exactly
 */
function textNodeTransform(node, editor, meetingId) {
  // Only process simple text nodes (like the guide's emoji example)
  if (!node.isSimpleText()) {
    return;
  }

  // Check if this TextNode is inside a HeadingNode
  const parent = node.getParent();
  if (!$isHeadingNode(parent)) {
    return;
  }

  const text = node.getTextContent();
  const siblings = parent.getChildren();
  const nodeIndex = siblings.indexOf(node);
  
  // Check if this is the last text node in the heading
  const isLastTextNode = nodeIndex === siblings.length - 1 || 
    (nodeIndex === siblings.length - 2 && siblings[siblings.length - 1].__type === 'detail-request-button');

  try {
    // If this is the last text node and there's content, ensure button exists
    if (isLastTextNode && text.trim() !== '') {
      const hasButton = siblings.some(child => child.__type === 'detail-request-button');
      
      if (!hasButton && !headingsWithButtons.has(parent)) {
        // Create our detail request button node with meeting ID and editor
        const detailRequestButtonNode = $createDetailRequestButtonNode(meetingId, editor);
        
        // Insert the button after this text node
        node.insertAfter(detailRequestButtonNode);
        
        // Mark this heading as having a button
        headingsWithButtons.add(parent);
      }
    }
    
    // If this text node is empty and there's a button, the text was deleted
    // Remove the button as well
    if (text.trim() === '' && siblings.length > 1) {
      const buttonNode = siblings.find(child => child.__type === 'detail-request-button');
      if (buttonNode) {
        buttonNode.remove();
        headingsWithButtons.delete(parent);
      }
    }
    
  } catch (error) {
    console.error('Error in textNodeTransform:', error);
  }
}

/**
 * Transform function for HeadingNodes to manage button lifecycle
 */
function headingNodeTransform(node) {
  try {
    const children = node.getChildren();
    const textNodes = children.filter(child => child.getType() === 'text');
    const buttonNodes = children.filter(child => $isDetailRequestButtonNode(child));
    
    // If heading has no text content, remove any buttons
    const hasTextContent = textNodes.some(textNode => textNode.getTextContent().trim() !== '');
    
    if (!hasTextContent && buttonNodes.length > 0) {
      buttonNodes.forEach(buttonNode => {
        buttonNode.remove();
      });
    }
    
    // Clean up tracking when heading is removed
    if (!hasTextContent && buttonNodes.length === 0) {
      headingsWithButtons.delete(node);
    }
    
  } catch (error) {
    console.error('Error in headingNodeTransform:', error);
  }
}

/**
 * Generic transform function to clean up detail request buttons from non-heading nodes
 * This handles the case when a heading is converted to any other block type
 */
function cleanupButtonsTransform(node) {
  try {
    const children = node.getChildren();
    const buttonNodes = children.filter(child => $isDetailRequestButtonNode(child));
    
    // Only headings should have detail request buttons - remove them from everything else
    if (buttonNodes.length > 0) {
      buttonNodes.forEach(buttonNode => {
        buttonNode.remove();
      });
      // Clean up tracking
      headingsWithButtons.delete(node);
    }
    
  } catch (error) {
    console.error('Error in cleanupButtonsTransform:', error);
  }
}

/**
 * Plugin registration function following the official guide pattern
 * A plugin is a function that accepts LexicalEditor and returns cleanup function
 */
export function registerHeaderDetailRequestPlugin(editor, meetingId) {
  // Create transform functions with meetingId and editor context
  const textTransformWithContext = (node) => textNodeTransform(node, editor, meetingId);
  const headingTransformWithContext = (node) => headingNodeTransform(node);
  
  // Register all transforms following the guide's pattern
  const unregisterTextTransform = editor.registerNodeTransform(TextNode, textTransformWithContext);
  const unregisterHeadingTransform = editor.registerNodeTransform(HeadingNode, headingTransformWithContext);
  
  // Register cleanup transforms for all non-heading block types
  const unregisterParagraphTransform = editor.registerNodeTransform(ParagraphNode, cleanupButtonsTransform);
  const unregisterQuoteTransform = editor.registerNodeTransform(QuoteNode, cleanupButtonsTransform);
  const unregisterListTransform = editor.registerNodeTransform(ListNode, cleanupButtonsTransform);
  const unregisterListItemTransform = editor.registerNodeTransform(ListItemNode, cleanupButtonsTransform);
  
  // No need for update listener with dynamic text retrieval approach
  
  // Return cleanup function that unregisters all transforms
  return () => {
    unregisterTextTransform();
    unregisterHeadingTransform();
    unregisterParagraphTransform();
    unregisterQuoteTransform();
    unregisterListTransform();
    unregisterListItemTransform();
    // No update listener to unregister
  };
}

/**
 * React wrapper component for the plugin
 * This makes it easy to use in React applications
 */
export default function HeaderDetailRequestPlugin({ meetingId }) {
  const [editor] = useLexicalComposerContext();
  
  useEffect(() => {
    return registerHeaderDetailRequestPlugin(editor, meetingId);
  }, [editor, meetingId]);
  
  return null;
}
